import AddLinkIcon from '@mui/icons-material/AddLink';
import { Alert } from '@mui/material';
import type { GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid-premium';
import { GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { captureException } from '@sentry/react';
import { type JSX, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { UserContext } from '~/auth/userContext';
import { InvoicePreviewPanel } from '~/components/invoices/InvoicePreviewPanel';
import { openInNewTab } from '~/components/rowPreview/RowPreviewPanel';
import { useTableRowPreview } from '~/components/rowPreview/useTableRowPreview';
import { getSpendingTableColumns } from '~/components/spending/spendingTableColumns';
import { LiftAITable } from '~/components/table';
import useInvoice from '~/data/hooks/useInvoice';
import useInvoices from '~/data/hooks/useInvoices';
import {
  invoiceOrderingParam,
  invoiceParamsFromFilterModel,
  invoiceWithPortfolio,
} from '~/data/hooks/useSpending';
import type { Invoice, InvoiceKindKey, InvoiceWithPortfolio } from '~/data/schemas/invoice';
import { InvoiceKind } from '~/data/schemas/invoice';
import { useDetailTableHeight } from '~/hooks/useDetailTableHeight';
import useLAISnackbar, { ActionEnum } from '~/hooks/useLAISnackbar';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';
import { useFeatureFlags } from '~/utils/featureFlags';
import { canAccess, NAVIGATE_TO_SPENDING_PAGE, PROPERTY_LINKS_IN_SPENDING } from '~/utils/role';
import { buildUrlForInvoice } from '~/utils/urlBuilder';

const DEFAULT_PAGE_SIZE = 25;

/** The table's own heading when a subtab has fixed the class. */
const INVOICE_KIND_TITLES: Record<InvoiceKindKey, string> = {
  [InvoiceKind.Invoice]: 'Invoices',
  [InvoiceKind.Proposal]: 'Proposals',
  [InvoiceKind.Credit]: 'Credits',
};

/**
 * Every invoice and proposal billed to a set of elevators, scoped server-side by the
 * `cars` filter. Keyed on car ids rather than on the surface asking, so an elevator's
 * own page shows its billing history with the same table.
 *
 * `relatedTo` is the invoice-page case: the invoice in view and anything already
 * linked to it are dropped, and each remaining row gets a Link action.
 */
export function ElevatorInvoicesTable({
  carIds,
  emptyMessage,
  relatedTo,
  kind,
}: {
  carIds: string[];
  /** Shown instead of the table when there are no cars to scope the read to. */
  emptyMessage: ReactNode;
  relatedTo?: Invoice;
  /**
   * Narrows the table to one class, for a caller that gives each its own subtab — the
   * subtab then owns the class, so the Class column stops being filterable. Omit to list
   * both classes together.
   */
  kind?: InvoiceKindKey;
}) {
  const tableHeight = useDetailTableHeight();
  const apiRef = useGridApiRef();
  const featureFlags = useFeatureFlags();
  const {
    currencyCode,
    userDetails: { role: userRole },
  } = useContext(UserContext);
  // The invoice page's own gate (`spendingAccessLoader`): the panel is that page in
  // miniature, and the elevator page hosting this table has no spending gate of its own.
  const canOpenInvoices =
    canAccess(userRole, NAVIGATE_TO_SPENDING_PAGE) &&
    (userRole !== 'client' || featureFlags.clientPortalSpending === true);
  const preview = useTableRowPreview({
    list: relatedTo ? 'invoice_related_invoices' : 'elevator_invoices',
    apiRef,
    enabled: canOpenInvoices,
  });
  const [searchParams] = useSearchParams();
  const terms = useLocaleTerms();
  const { showEntityActionSnackbar } = useLAISnackbar();

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  // Sorting goes to the server for the same reason the filtering does: sorted in the
  // browser it would only reorder the page in hand, so the first row of a descending
  // sort would be the largest of twenty-five rather than of the whole list.
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'date', sort: 'desc' }]);

  const onSortModelChange = useCallback((model: GridSortModel) => {
    setSortModel(model);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const onFilterModelChange = useCallback((model: GridFilterModel) => {
    setFilterModel(model);
    // A narrower list is shorter — page 4 of the old one may not exist.
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const { invoices, metadata, isLoading } = useInvoices(
    {
      // Filtering goes to the server, like the paging: applied in the browser it would
      // narrow the page in hand and leave the count and the pages after it untouched.
      ...invoiceParamsFromFilterModel(filterModel),
      ordering: invoiceOrderingParam(sortModel) ?? '-date',
      cars: carIds,
      // After the spread: where a subtab has fixed the class, it wins over
      // anything the grid could hold for that column.
      ...(kind ? { kind: [kind] } : {}),
      // `API_InvoiceFilterSearchParams` takes these as strings and coerces them
      // (the spending grid feeds them straight from URL query params).
      limit: String(paginationModel.pageSize),
      offset: String(paginationModel.page * paginationModel.pageSize),
    },
    carIds.length > 0,
  );

  // Passing `null` holds the read; the elevator page has no invoice to relate to.
  const { updateRelatedInvoice } = useInvoice(relatedTo?.id ?? null);

  const linkedIds = useMemo(
    () => new Set((relatedTo?.relatedInvoices ?? []).map((related) => related.id)),
    [relatedTo?.relatedInvoices],
  );

  const rows = useMemo<InvoiceWithPortfolio[]>(
    () =>
      (invoices ?? [])
        .filter((row) => row.id !== relatedTo?.id && !linkedIds.has(row.id))
        .map(invoiceWithPortfolio),
    [invoices, relatedTo?.id, linkedIds],
  );

  // How many of the server's rows this table dropped, counted from the rows in hand
  // rather than from `relatedTo`'s link list. The two are not the same number: the
  // linked invoices can sit outside this car-scoped, class-scoped query entirely, and
  // subtracting them from the server total anyway shortened the pager until the last
  // page could not be reached.
  const excludedFromCount = (invoices ?? []).length - rows.length;

  // The list is rebuilt from `relatedTo` at call time rather than captured, so two Link
  // presses in quick succession can't have the second write a list that predates the
  // first.
  const onLink = useCallback(
    async (invoiceId: string) => {
      if (!relatedTo) return;
      try {
        await updateRelatedInvoice([
          ...(relatedTo.relatedInvoices ?? []).map((related) => related.id),
          invoiceId,
        ]);
      } catch (error) {
        captureException(error);
        showEntityActionSnackbar(
          { name: 'Invoice', action: ActionEnum.Fail, id: undefined },
          { variant: 'error' },
        );
      }
    },
    [updateRelatedInvoice, relatedTo, showEntityActionSnackbar],
  );

  const columns = useMemo(
    () =>
      getSpendingTableColumns({
        currencyCode,
        canFilterProperty: false,
        canFilterClass: kind === undefined,
        canLinkToProperty: canAccess(userRole, PROPERTY_LINKS_IN_SPENDING),
        terms,
        showDisputeManagement: featureFlags.disputeManagement === true,
        // Nothing to link to without an invoice in view.
        additionalColumns: relatedTo
          ? [
              {
                field: 'actions',
                type: 'actions',
                getActions: ({ row }): JSX.Element[] => [
                  <GridActionsCellItem
                    key="link"
                    icon={<AddLinkIcon />}
                    label="Link to this invoice"
                    onClick={() => void onLink(row.id)}
                    showInMenu
                  />,
                ],
                width: 70,
                align: 'right',
              },
            ]
          : [],
      }),
    [currencyCode, userRole, terms, featureFlags.disputeManagement, onLink, relatedTo, kind],
  );

  // A new tab rather than a navigation: this list is read against the page it sits on,
  // and replacing that page with one of its rows loses what the comparison was for.
  const rowHref = useCallback(
    (row: InvoiceWithPortfolio) => buildUrlForInvoice(row.id, searchParams),
    [searchParams],
  );

  if (carIds.length === 0) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  return (
    <>
      <LiftAITable<InvoiceWithPortfolio>
        height={tableHeight}
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        // The server count, minus the rows filtered out of this page. Paging is still
        // server-side, so a page can come back short.
        rowCount={Math.max((metadata?.numInvoices ?? rows.length) - excludedFromCount, rows.length)}
        loading={isLoading}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        tableHeaderTitle={kind ? INVOICE_KIND_TITLES[kind] : 'Spendings'}
        rowHref={rowHref}
        initialState={{
          pinnedColumns: { right: relatedTo ? ['actions'] : [] },
          columns: {
            columnVisibilityModel: {
              property: false,
              portfolio: false,
              region: false,
              consultant: false,
            },
          },
        }}
        toolbarProps={{ filterControls: preview.peekSwitch }}
        {...preview.tableProps}
      />
      {preview.enabled ? (
        <InvoicePreviewPanel
          rowPreview={preview.rowPreview}
          onOpenFullPage={(id) => openInNewTab(buildUrlForInvoice(id, searchParams))}
        />
      ) : null}
    </>
  );
}
